/*
 * Copyright (c) 2025, Gather
 * https://gather.life
 */

import type { PaletteOptions } from '@mui/material/styles/createPalette';

// Material UI theme color palettes
// This includes colors that Material UI will generate automatically based on the theme as well as
// custom, additional ones
// To be kept in sync with src/assets/styles/color.util.scss
export const GatherLightPalette = {
  mode: 'light',
  // Primary Colors
  ElectricYellow: {
    100: '#FBFAEE',
    200: '#F5F4E5',
    300: '#F0EBA8',
    400: '#E4D947',
    500: '#D0C41F',
    600: '#A19817',
    700: '#706A10',
  },
  Jade: {
    100: '#F2F8F5',
    200: '#EBF4F0',
    300: '#C9EDDC',
    400: '#81CFAA',
    500: '#3EAE78',
    600: '#2E8A5D',
    700: '#236143',
  },
  Neutral: {
    100: '#FEFCFC',
    200: '#F3F3F3',
    300: '#DADADA',
    400: '#909090',
    500: '#666666',
    600: '#434343',
    700: '#272727',
  },
  // Alternates
  Champagne: {
    100: '#FAF5EF',
    200: '#F3EAE2',
    300: '#E1D3C7',
    400: '#CCB199',
    500: '#C2A487',
    600: '#B28B66',
    700: '#886544',
  },
  CloudPink: {
    100: '#FEF6F9',
    200: '#F4ECEE',
    300: '#EBD1DB',
    400: '#E3ADC1',
    500: '#D689A7',
    600: '#C65C86',
    700: '#98345B',
  },
  PaleDogwood: {
    100: '#FAF4F0',
    200: '#F4ECE6',
    300: '#E2CFC1',
    400: '#CEAE97',
    500: '#C59F84',
    600: '#B68563',
    700: '#8B6041',
  },
  Poppy: {
    100: '#FDF2F2',
    200: '#F5EBEB',
    300: '#EFC8C9',
    400: '#EEA0A1',
    500: '#E6797C',
    600: '#DD4649',
    700: '#A72527',
  },
  SeaSalt: {
    100: '#F5F8F2',
    200: '#EBEEE7',
    300: '#D4DEC4',
    400: '#AFBF97',
    500: '#98AE7A',
    600: '#697A52',
    700: '#4F5B3E',
  },
  Sunrise: {
    100: '#FBF5EE',
    200: '#F7EFE8',
    300: '#F2CAA6',
    400: '#F4B071',
    500: '#F59E4E',
    600: '#E88830',
    700: '#B86314',
  },
  // Feedback
  Magenta: {
    100: '#FAF0F1',
    200: '#FAE5E9',
    300: '#FFB2C0',
    400: '#FF859B',
    500: '#FF5271',
    600: '#F4254A',
    700: '#8F2437',
  },
  Yellow: {
    100: '#F9F5EC',
    200: '#FAF1DB',
    300: '#FFDE8A',
    400: '#FFC93E',
    500: '#F2AE02',
    600: '#D08F01',
    700: '#6C5313',
  },
} as const;

export const GatherLightGradientTokens = {
  mode: 'light',
  // Gradients
  alert: `linear-gradient(90deg, ${GatherLightPalette.Yellow[400]} 0%, ${GatherLightPalette.Sunrise[500]} 100%)`,
  critical: `linear-gradient(270deg, ${GatherLightPalette.Magenta[500]} -0.3%, ${GatherLightPalette.Magenta[200]} 100%)`,
  good: `linear-gradient(272deg, ${GatherLightPalette.ElectricYellow[300]} -32.23%, ${GatherLightPalette.Jade[500]} 147.47%)`, // V3
  goodV1: 'linear-gradient(267deg, #D3E8A9 4.31%, #73C38A 95.69%)', // V1
  goodV2: `fill: linear-gradient(267deg, #D3E8A9 4.31%, ${GatherLightPalette.Jade[500]} 95.69%)`,
  primary: `radial-gradient(238.28% 165.91% at 95.19% 4.38%, #CDE9DC 0%, ${GatherLightPalette.SeaSalt[300]} 36.39%, #F0B075 68.17%, #C89F8D 100%)`,
  secondary: `radial-gradient(408.49% 118.48% at 76.34% 7.63%, ${GatherLightPalette.Jade[300]} 0.52%, ${GatherLightPalette.ElectricYellow[300]} 60%)`,
  warning: `radial-gradient(405.31% 173.58% at 100% 0%, #FF9630 0%, #FFCBB4 40.63%, ${GatherLightPalette.ElectricYellow[400]} 100%);`,
};

export const GatherLightSystemTokens = {
  // System Tokens
  OnSurface: {
    accent: GatherLightPalette.Jade[500],
    critical: GatherLightPalette.Magenta[600],
    disabled: GatherLightPalette.Neutral[400],
    good: GatherLightPalette.Jade[500],
    inverse: GatherLightPalette.Neutral[100],
    primary: GatherLightPalette.Neutral[700],
    recommendedHighlight: '#F9F9F9',
    secondary: GatherLightPalette.Neutral[500],
    skeleton: '#F3F1F1',
    tertiary: GatherLightPalette.Neutral[400],
    warning: GatherLightPalette.Yellow[500],
  },
  OnSurfaceAccent: {
    accent: GatherLightPalette.Neutral[700],
  },
  OnSurfaceAction: {
    disabled: GatherLightPalette.Neutral[400],
    primary: GatherLightPalette.Neutral[700],
    secondary: GatherLightPalette.Neutral[700],
    tertiary: GatherLightPalette.Neutral[100],
  },
  OnSurfaceState: {
    critical: GatherLightPalette.Neutral[100],
    criticalContainer: '#D40328',
    good: GatherLightPalette.Neutral[100],
    goodAccent: '#6AA06C',
    goodContainer: '#207E51',
    warning: GatherLightPalette.Neutral[700],
    warningContainer: '#903D04',
  },
  OnSurfaceTheme: {
    paleDogwood: '#4E3E32',
  },
  Outline: {
    disabled: `${GatherLightPalette.Neutral[700]}0D`,
    inverse: GatherLightPalette.Neutral[100],
    primary: GatherLightPalette.Neutral[700],
    secondary: `${GatherLightPalette.Neutral[700]}80`,
    tertiary: `${GatherLightPalette.Neutral[700]}26`,
  },
  OutlineInput: {
    active: GatherLightPalette.Neutral[700],
    critical: GatherLightPalette.Magenta[600],
    disabled: GatherLightPalette.Neutral[300],
    enabled: `${GatherLightPalette.Neutral[700]}26`,
    focused: GatherLightPalette.Jade[500],
    inverse: GatherLightPalette.Neutral[100],
    pressed: GatherLightPalette.Neutral[400],
    primary: GatherLightPalette.Neutral[700],
    secondary: `${GatherLightPalette.Neutral[700]}80`,
    selected: GatherLightPalette.ElectricYellow[500],
    tertiary: `${GatherLightPalette.Neutral[700]}26`,
  },
  OutlineTheme: {
    cloudPink: GatherLightPalette.CloudPink[400],
    electricYellow: GatherLightPalette.ElectricYellow[500],
    jade: GatherLightPalette.Jade[500],
    paleDogwood: GatherLightPalette.PaleDogwood[300],
    seaSalt: GatherLightPalette.SeaSalt[400],
  },
  Surface: {
    bright: GatherLightPalette.Neutral[100],
    default: GatherLightPalette.Neutral[200],
    dim: GatherLightPalette.Neutral[300],
    disabled: `${GatherLightPalette.Neutral[700]}0D`,
    inverse: GatherLightPalette.Neutral[700],
    offset: `${GatherLightPalette.Neutral[700]}0D`,
    scrim: `${GatherLightPalette.Neutral[700]}66`,
  },
  SurfaceAccent: {
    accent: GatherLightPalette.ElectricYellow[400],
  },
  SurfaceAction: {
    disabled: `${GatherLightPalette.Neutral[700]}0D`,
    primaryEnabled: GatherLightPalette.ElectricYellow[400],
    primaryPressed: GatherLightPalette.ElectricYellow[300],
    secondaryEnabled: GatherLightPalette.Neutral[100],
    secondaryPressed: GatherLightPalette.Neutral[200],
    tertiaryEnabled: GatherLightPalette.Neutral[700],
    tertiaryPressed: GatherLightPalette.Neutral[600],
  },
  SurfaceContainer: {
    default: GatherLightPalette.Neutral[100],
    dim: GatherLightPalette.Neutral[200],
  },
  SurfaceStateCritical: {
    critical: GatherLightPalette.Magenta[600],
    criticalContainer: GatherLightPalette.Magenta[200],
  },
  SurfaceStateGood: {
    good: GatherLightPalette.Jade[500],
    goodContainer: GatherLightPalette.Jade[200],
  },
  SurfaceStateWarning: {
    warning: GatherLightPalette.Yellow[400],
    warningContainer: GatherLightPalette.Yellow[200],
  },
  SurfaceTheme: {
    cloudPink: '#F0EAEC',
    jade: '#E7EEEB',
    paleDogwood: '#F1EDE9',
    paleDogwoodMain: GatherLightPalette.PaleDogwood[400],
    seaSalt: GatherLightPalette.SeaSalt[200],
    tintYellow: '#F1F0EA',
  },
} as const;

export const GatherLightVehicleStatusTokens = {
  critical: {
    onPrimary: GatherLightSystemTokens.Surface.bright,
    primary: GatherLightSystemTokens.SurfaceStateCritical.critical,
    status: GatherLightGradientTokens.critical,
  },
  good: {
    onPrimary: GatherLightSystemTokens.Surface.bright,
    primary: GatherLightSystemTokens.SurfaceStateGood.good,
    status: GatherLightGradientTokens.good,
  },
  mode: 'light',
  warning: {
    onPrimary: GatherLightSystemTokens.Surface.inverse,
    primary: GatherLightSystemTokens.SurfaceStateWarning.warning,
    status: GatherLightGradientTokens.warning,
  },
};

// Material UI theme color definitions
export const gatherLightPaletteOptions: PaletteOptions = {
  action: {
    active: GatherLightSystemTokens.SurfaceAction.primaryPressed,
    disabled: GatherLightSystemTokens.OnSurfaceAction.disabled,
    disabledBackground: GatherLightSystemTokens.SurfaceAction.disabled,
    focus: GatherLightSystemTokens.OutlineInput.focused,
    hover: GatherLightSystemTokens.SurfaceAction.primaryPressed,
    selected: GatherLightSystemTokens.SurfaceAction.primaryPressed,
  },
  background: {
    default: GatherLightSystemTokens.Surface.default,
    paper: GatherLightSystemTokens.Surface.bright,
  },
  common: {
    black: GatherLightPalette.Neutral[700],
    white: GatherLightPalette.Neutral[100],
  },
  divider: GatherLightSystemTokens.Outline.tertiary,
  error: {
    main: GatherLightPalette.Magenta[600],
  },
  grey: {
    ...GatherLightPalette.Neutral,
  },
  info: {
    main: GatherLightPalette.Sunrise[500],
  },
  mode: GatherLightPalette.mode,
  primary: {
    main: GatherLightPalette.ElectricYellow[400],
  },
  secondary: {
    main: GatherLightPalette.Jade[500],
  },
  success: {
    main: GatherLightPalette.Jade[500],
  },
  text: {
    disabled: GatherLightSystemTokens.OnSurface.disabled,
    primary: GatherLightSystemTokens.OnSurface.primary,
    secondary: GatherLightSystemTokens.OnSurface.secondary,
  },
  warning: {
    main: GatherLightPalette.Yellow[500],
  },
};
